import { ChevronDownIcon } from "@brandclub/common-ui";
import { Collapse, styled, Skeleton } from "@mui/material";
import React from "react";

const StyledCollapsibleSection = styled("div")<{
  disabled?: boolean;
}>(({ disabled }) => ({
  overflow: "hidden",
  opacity: disabled ? 0.5 : 1,
}));

export const SectionTitle = styled("div")(({ theme }) => ({
  fontSize: 20,
  fontWeight: 600,
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: 16,
  [theme.breakpoints.down("md")]: {
    fontSize: 14,
    fontWeight: 600,
    opacity: 0.5,
  },
}));

const Header = styled("button")<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    border: "none",
    cursor: disabled ? "default" : "pointer",
    background: "transparent",
    color: theme.palette.primary.main,
    lineHeight: "32px",
    padding: 0,
    textAlign: "left",
  })
);

const Content = styled("div")(({ theme }) => ({
  paddingTop: 16,
  [theme.breakpoints.up("md")]: {
    fontSize: "16px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
  },
}));

const CollapsedContent = styled("div")<{ disabled?: boolean }>(({ theme }) => ({
  opacity: 1,
  paddingTop: 16,
  [theme.breakpoints.up("md")]: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "24px",
    textAlign: "left",
  },
  [theme.breakpoints.down("md")]: {
    paddingTop: 10,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "18px",
    textAlign: "left",
  },
}));

const CollapsibleSection: React.FC<{
  isOpen: boolean;
  onToggle?: () => void;
  title: string | React.ReactNode;
  titleIcon?: React.ReactNode;
  collapsedContent?: string | React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  hideControl?: boolean;
  style?: React.CSSProperties;
  loading?: boolean;
}> = ({
  isOpen,
  onToggle,
  title,
  titleIcon,
  children,
  disabled,
  hideControl = false,
  collapsedContent,
  style,
  loading,
}) => {
  if (loading) {
    return (
      <StyledCollapsibleSection disabled style={style}>
        <Header disabled={true}>
          <SectionTitle>
            <Skeleton
              variant="rectangular"
              width={200}
              height={24}
              sx={{ borderRadius: "4px" }}
            />
          </SectionTitle>
          {!hideControl && (
            <div>
              <ChevronDownIcon
                style={{
                  transition: "transform 0.3s",
                  width: "33px",
                  height: "33px",
                  transform: "rotate(0deg)",
                  opacity: 0.5,
                }}
              />
            </div>
          )}
        </Header>
        <Collapse in={true}>
          <CollapsedContent disabled={disabled}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={24}
              sx={{ borderRadius: "4px" }}
            />
          </CollapsedContent>
        </Collapse>
      </StyledCollapsibleSection>
    );
  }

  return (
    <StyledCollapsibleSection disabled={disabled} style={style}>
      <Header disabled={disabled} onClick={onToggle}>
        <SectionTitle>
          {title}
          {titleIcon}
        </SectionTitle>
        {!hideControl && (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
      </Header>
      {collapsedContent ? (
        <Collapse in={!isOpen}>
          <CollapsedContent disabled={disabled}>
            {collapsedContent}
          </CollapsedContent>
        </Collapse>
      ) : null}
      <Collapse in={isOpen}>
        <Content>{children}</Content>
      </Collapse>
    </StyledCollapsibleSection>
  );
};

export default CollapsibleSection;
